import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/Common";
import { CardState, Eyebrow, Field, SubHeader, WbCard, WisdomQuote } from "@/components/wb/WbUi";
import { api } from "@/lib/api";
import { useWb, wbError } from "@/lib/wb";
import { cn } from "@/lib/utils";

const status = (p = {}) => (p.completed_at ? "Completed · revisit any time" : p.started ? `Day ${p.next_day || p.done_count + 1} of ${p.total || 5} · ${p.done_count || 0} done` : `${p.total || 5} days · not started`);

export const Journeys = () => {
  const q = useWb("/wellbeing/journeys");
  return (
    <div className="space-y-6" data-testid="wb-journeys">
      <SubHeader title="Wisdom Journeys" subtitle="Guided five-day series. Go at your own pace: no timer, no streak." back="/wellbeing/wisdom" />
      <CardState q={q} what="Wisdom Journeys"><div className="grid gap-4 md:grid-cols-2">{(q.data?.journeys || []).map((j) => {
        const p = j.progress || {}, pct = Math.round(((p.done_count || 0) / (p.total || 5)) * 100);
        return (
          <Link key={j.content_id} to={`/wellbeing/journey/${j.content_id}`} className="fade-up block rounded-[20px] border border-[#27272A] bg-[#121214] p-5 transition-colors hover:border-[#D4AF37]/50" data-testid={`wb-journey-${j.content_id}`}>
            <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-[#33290D] text-[#D4AF37]"><Map className="h-5 w-5" /></span>
            <p className="font-wb text-xl text-zinc-100">{j.title}</p>
            <p className="text-sm text-zinc-400">{j.subtitle}</p>
            {j.inspired_by?.length > 0 && <p className="mt-2 text-xs text-zinc-500">Inspired by {j.inspired_by.join(", ")}</p>}
            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-[#27272A]"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${pct}%` }} /></div>
            <p className="mt-2 text-xs text-[#F3C649]" data-testid={`wb-journey-status-${j.content_id}`}>{status(p)}</p>
          </Link>);
      })}</div></CardState>
    </div>
  );
};

export const Journey = () => {
  const { id } = useParams();
  const q = useWb(`/wellbeing/journeys/${id}`);
  const [sel, setSel] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const d = q.data, days = d?.days || [];
  const started = d?.progress?.started || days.some((x) => x.status === "done");
  const cur = days.find((x) => x.day === sel) || days.find((x) => x.status === "open") || days[0];
  const act = async (path, body, msg) => {
    setBusy(true);
    try { const { data } = await api.post(path, body); q.setData(data); if (msg) toast.success(msg); setText(""); setSel((data?.days || []).find((x) => x.status === "open")?.day || null); }
    catch (e) { e.response?.status === 409 ? toast("Finish the previous day first. There's no rush.") : wbError(e); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="wb-journey">
      <SubHeader title={d?.title || "Wisdom Journey"} subtitle={d?.subtitle} back="/wellbeing/journeys" />
      <CardState q={q} what="This journey">
        {!started ? (
          <WbCard gold data-testid="wb-journey-intro"><p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{d?.intro}</p><Button className="mt-5 rounded-full" disabled={busy} onClick={() => act(`/wellbeing/journeys/${id}/start`)} data-testid="wb-journey-begin">{busy ? <Spinner /> : "Begin the journey"}</Button></WbCard>
        ) : <>
          {d?.progress?.completed_at && <WbCard gold data-testid="wb-journey-complete"><p className="font-wb text-xl text-zinc-100">You completed this journey</p><Button variant="outline" className="mt-3 rounded-full" disabled={busy} onClick={() => act(`/wellbeing/journeys/${id}/restart`, {}, "A fresh start. Your past entries are kept.")} data-testid="wb-journey-restart">Walk it again</Button></WbCard>}
          <div className="flex flex-wrap gap-2" data-testid="wb-journey-days">{days.map((x) => (
            <button key={x.day} disabled={x.status === "locked"} onClick={() => setSel(x.day)} className={cn("inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm transition-colors", cur?.day === x.day ? "border-[#D4AF37] bg-[#33290D] text-[#F3C649]" : x.status === "locked" ? "border-[#27272A] text-zinc-600" : "border-[#27272A] text-zinc-300 hover:border-zinc-500")} data-testid={`wb-journey-day-${x.day}`}>
              {x.status === "done" ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : x.status === "locked" && <Lock className="h-3.5 w-3.5" />}Day {x.day}
            </button>))}</div>
          {cur && <div className="grid gap-6 lg:grid-cols-2">
            <WbCard data-testid="wb-journey-reading"><Eyebrow>Day {cur.day} · {cur.theme}</Eyebrow><p className="font-wb text-xl text-zinc-100">{cur.title}</p><p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{cur.reading}</p>{cur.exercise && <Field label="Try this today">{cur.exercise}</Field>}</WbCard>
            <div className="space-y-6">
              {cur.wisdom && <WbCard gold><WisdomQuote item={cur.wisdom} /></WbCard>}
              <WbCard data-testid="wb-journey-reflection"><Eyebrow>Reflection</Eyebrow><p className="font-wb text-lg text-zinc-100">{cur.reflection}</p>
                {cur.status === "done" ? <p className="mt-3 whitespace-pre-wrap rounded-xl bg-black/30 p-3 text-sm text-zinc-300" data-testid="wb-journey-answer">{cur.answer}</p> : cur.status === "open" && <>
                  <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write what comes up for you…" className="mt-3 bg-[#09090B]" data-testid="wb-journey-input" />
                  <Button className="mt-3 rounded-full" disabled={!text.trim() || busy} onClick={() => act(`/wellbeing/journeys/${id}/days/${cur.day}/complete`, { text: text.trim() }, `Day ${cur.day} complete`)} data-testid="wb-journey-finish">{busy ? <Spinner /> : `Finish day ${cur.day}`}</Button>
                </>}
              </WbCard>
            </div>
          </div>}
        </>}
      </CardState>
    </div>
  );
};
